import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, Menu, MenuItem, Pagination, Snackbar, Tab, Tabs } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { CopyComponentsFlow } from "../features/ImportFlow/CopyComponentsFlow"
import { Scorecardset } from "../features/ScoreCardSets/Scorecardset"
import { ScoreTable } from "../features/ScoreTables"
import { ScoreCardLoader } from "../features/Scorecards/components/ScoreCardLoader"
import { Scorecard } from "../features/Scorecards/components/Scorecard"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { PaginatedScoreCardListList } from "../types/generated/api/PaginatedScoreCardListList"
import type { PaginatedScoreCardSetListList } from "../types/generated/api/PaginatedScoreCardSetListList"
import { ScoreCardList } from "../types/generated/api/ScoreCardList"
import type { ScoreCardSetList } from "../types/generated/api/ScoreCardSetList"
import { useIsPermitted } from "../utils/PermissionsHelpers"

interface ScorecardsTabProps {
  shouldShowNewScorecard: boolean
  onNewScoreCardCancel: () => void
  shouldShowNewScoreTable: boolean
  onNewScoreTableCancel: () => void
  duplicatedScoreCard?: ScoreCardList | null
  setDuplicatedScoreCard?: React.Dispatch<React.SetStateAction<ScoreCardList | null>>
  subHeaderActionButtons?: React.ReactElement[]
}

function ScorecardsTab(props: Readonly<ScorecardsTabProps>): React.ReactElement {
  const {
    onNewScoreCardCancel,
    onNewScoreTableCancel,
    shouldShowNewScoreTable,
    shouldShowNewScorecard,
    duplicatedScoreCard,
    setDuplicatedScoreCard,
    subHeaderActionButtons,
  } = props

  const { t } = useTranslation("scorecards")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve scorecard", permissions.konan, flattenedKonanPermissions)
  const canAddScorecard = useIsPermitted("Add scorecard", permissions.konan, flattenedKonanPermissions)

  const { id: projectId } = useParams<{ id: string }>()

  // Pagination
  const pageSize = 6
  const [page, setPage] = useState<number>(0)

  //SSF States
  const [searchValue, setSearchValue] = useState<string>("")

  /**
   * Fetches scorecards data for the current project.
   *
   * @returns An object containing the scorecards data and loading status.
   */
  const {
    data: scorecards,
    isLoading: isScorecardsLoading,
    isFetching: isScorecardsFetching,
  } = useQuery<AxiosResponse<PaginatedScoreCardListList>, AxiosError>(
    ["scorecards", projectId, page, pageSize, searchValue],
    () => KonanAPI.fetchScorecards(projectId as string, page + 1, pageSize, searchValue),
    {
      enabled: !!projectId,
    },
  )

  const duplicateScoreCard = (scorecard: ScoreCardList): void => {
    const newScoreCard = { ...scorecard, name: `${scorecard.name} - Copy` }
    setDuplicatedScoreCard?.(newScoreCard)
    // scroll to top
    window.scrollTo(0, 0)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} mb={2}>
        <KonanSubHeader
          containerType="Scorecard"
          searchValue={searchValue}
          setSearchValue={(value) => {
            setPage(() => 0)
            setSearchValue(() => value as string)
          }}
          isDebouncedInput
          actions={canAddScorecard && canShowDetails ? subHeaderActionButtons : undefined}
        />
      </Grid>
      {!canShowDetails && (
        <Grid item flex={1}>
          <Snackbar
            fullWidth
            severity="info"
            title={t("cannotShowDetailsSnackBar.title")}
            description={t("cannotShowDetailsSnackBar.description")}
          />
        </Grid>
      )}

      {(shouldShowNewScoreTable ||
        (duplicatedScoreCard && duplicatedScoreCard.format === ScoreCardList.format.TABLE)) && (
        <Grid item xs={12}>
          <ScoreTable
            scorecard={duplicatedScoreCard as ScoreCardList}
            isCreateMode
            handleCancel={duplicatedScoreCard ? () => setDuplicatedScoreCard?.(null) : onNewScoreTableCancel}
            goToFirstPage={() => setPage(0)}
            isDuplicateMode={!!duplicatedScoreCard}
          />
        </Grid>
      )}

      {(shouldShowNewScorecard ||
        (duplicatedScoreCard && duplicatedScoreCard.format === ScoreCardList.format.LIST)) && (
        <Grid item xs={12}>
          <Scorecard
            scorecard={duplicatedScoreCard as ScoreCardList}
            isCreateMode
            handleCancel={duplicatedScoreCard ? () => setDuplicatedScoreCard?.(null) : onNewScoreCardCancel}
            goToFirstPage={() => setPage(0)}
          />
        </Grid>
      )}

      {isScorecardsLoading || isScorecardsFetching
        ? [1, 2].map((item: number) => (
            <Grid item xs={12} key={item}>
              <ScoreCardLoader />
            </Grid>
          ))
        : scorecards?.data?.results?.length && scorecards?.data?.results?.length > 0
          ? scorecards?.data.results.map((scorecard: ScoreCardList) => (
              <Grid item xs={12} key={scorecard.uuid}>
                {scorecard.format === ScoreCardList.format.LIST ? (
                  <Scorecard
                    scorecard={scorecard}
                    disableDuplicateButton={!!duplicatedScoreCard || shouldShowNewScorecard || shouldShowNewScoreTable}
                    duplicateScoreCard={duplicateScoreCard}
                  />
                ) : (
                  <ScoreTable
                    scorecard={scorecard}
                    disableDuplicateButton={!!duplicatedScoreCard || shouldShowNewScorecard || shouldShowNewScoreTable}
                    duplicateScoreTable={duplicateScoreCard}
                  />
                )}
              </Grid>
            ))
          : !shouldShowNewScoreTable &&
            !shouldShowNewScorecard && (
              <Grid item xs={12} className={"empty-container"}>
                {!searchValue ? (
                  <KonanEmptyState
                    title={t("emptyState.scorecards.title")}
                    subTitle={t("emptyState.scorecards.subtitle")}
                  />
                ) : (
                  <KonanEmptyState
                    title={t("searchEmptyState.title", {
                      value: searchValue,
                    })}
                    subTitle={t("searchEmptyState.subtitle")}
                  />
                )}
              </Grid>
            )}

      {/* Pagination */}
      {!isScorecardsLoading && scorecards?.data.results && scorecards?.data.results.length > 0 && (
        <Grid item>
          {/* TODO: remove this when the bug is fixed */}
          <Pagination
            page={page + 1}
            pageSize={pageSize}
            totalCount={scorecards?.data.count as number}
            onPageChange={(page: number) => {
              setPage(page - 1)
              window.scrollTo(0, 0)
            }}
            variant={"complex"}
            hidePageSize={true}
          />
        </Grid>
      )}
    </Grid>
  )
}

interface ScoreSetsTabProps {
  shouldShowNewScoreSet: boolean
  onNewScoreSetCancel: () => void
  duplicatedScoreCard?: ScoreCardSetList | null
  setDuplicatedScoreCard?: React.Dispatch<React.SetStateAction<ScoreCardSetList | null>>
  subHeaderActionButtons?: React.ReactElement[]
}

function ScoreSetsTab(props: Readonly<ScoreSetsTabProps>): React.ReactElement {
  const {
    onNewScoreSetCancel,
    shouldShowNewScoreSet,
    duplicatedScoreCard,
    setDuplicatedScoreCard,
    subHeaderActionButtons,
  } = props

  const { t } = useTranslation("scorecards")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve scorecardset", permissions.konan, flattenedKonanPermissions)
  const canAddScorecardset = useIsPermitted("Add scorecardset", permissions.konan, flattenedKonanPermissions)

  const { id: projectId } = useParams<{ id: string }>()

  // Pagination
  const pageSize = 6
  const [page, setPage] = useState<number>(0)

  //SSF States
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<SortKey>("mostRecent")
  // SSF Types And Constants
  type SortKey = keyof typeof SORT_OPTIONS
  const SORT_OPTIONS = {
    mostRecent: "-created_at",
    lastUsed: "-last_used",
  } as const

  const duplicateScoreSet = (scoreSet: ScoreCardSetList): void => {
    const newScoreSet = { ...scoreSet, name: `${scoreSet.name} - Copy` }
    setDuplicatedScoreCard?.(newScoreSet)
    // scroll to top
    window.scrollTo(0, 0)
  }

  /**
   * Fetches the scorecard sets for the current project.
   *
   * @returns An object containing the loading state and data of the scorecard sets.
   */
  const {
    data: scorecardsets,
    isLoading: isSetsLoading,
    isFetching: isSetsFetching,
  } = useQuery<AxiosResponse<PaginatedScoreCardSetListList>, AxiosError>(
    ["scorecardsets", projectId, page, pageSize, searchValue, sortValue],
    () =>
      KonanAPI.fetchScorecardsets({
        projectId: projectId as string,
        page: page + 1,
        pageSize: pageSize,
        search: searchValue,
        ordering: SORT_OPTIONS[sortValue],
      }),
    {
      enabled: !!projectId,
    },
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} mb={2}>
        <KonanSubHeader
          containerType="Scorecard Set"
          sortValue={t(sortValue)}
          setSortValue={(value) => {
            setPage(() => 0)
            setSortValue(value as SortKey)
          }}
          sortOptions={[
            { label: t("mostRecent"), value: "mostRecent" },
            { label: t("lastUsed"), value: "lastUsed" },
          ]}
          searchValue={searchValue}
          setSearchValue={(value) => {
            setPage(() => 0)
            setSearchValue(() => value as string)
          }}
          isDebouncedInput
          actions={canShowDetails && canAddScorecardset ? subHeaderActionButtons : undefined}
        />
      </Grid>

      {!canShowDetails && (
        <Grid item flex={1}>
          <Snackbar
            fullWidth
            severity="info"
            title={t("cannotShowDetailsSnackBar.title")}
            description={t("cannotShowDetailsSnackBar.description")}
          />
        </Grid>
      )}

      {(shouldShowNewScoreSet || duplicatedScoreCard) && (
        <Grid item xs={12}>
          <Scorecardset
            isDuplicate={!!duplicatedScoreCard}
            isLoading={isSetsLoading}
            isCreate={true}
            handleCancel={!!duplicatedScoreCard ? () => setDuplicatedScoreCard?.(null) : () => onNewScoreSetCancel()}
            goToFirstPage={() => setPage(0)}
            uuid={duplicatedScoreCard?.uuid ?? undefined}
          />
        </Grid>
      )}

      {isSetsLoading || isSetsFetching
        ? [1, 2].map((item: number) => (
            <Grid item xs={12} key={item}>
              <ScoreCardLoader />
            </Grid>
          ))
        : scorecardsets?.data?.results?.length && scorecardsets?.data?.results?.length > 0
          ? scorecardsets?.data.results.map((set: ScoreCardSetList) => (
              <Grid item xs={12} key={set.uuid}>
                <Scorecardset
                  uuid={set.uuid}
                  isLoading={isSetsLoading}
                  goToFirstPage={() => setPage(0)}
                  disableDuplicateButton={!!duplicatedScoreCard || shouldShowNewScoreSet}
                  duplicateScoreSet={() => duplicateScoreSet(set)}
                  metaData={set}
                />
              </Grid>
            ))
          : !shouldShowNewScoreSet && (
              <Grid item xs={12} className={"empty-container"}>
                {!searchValue ? (
                  <KonanEmptyState
                    title={t("emptyState.scorecardsets.title")}
                    subTitle={t("emptyState.scorecardsets.subtitle")}
                  />
                ) : (
                  <KonanEmptyState title={t("searchEmptyState.title")} subTitle={t("searchEmptyState.subtitle")} />
                )}
              </Grid>
            )}

      {/* Pagination */}
      {!isSetsLoading && scorecardsets?.data.results && scorecardsets?.data.results.length > 0 && (
        <Grid item container>
          <Grid item>
            <Pagination
              page={page + 1}
              pageSize={pageSize}
              totalCount={scorecardsets?.data.count as number}
              onPageChange={(page: number) => {
                setPage(page - 1)
                window.scrollTo(0, 0)
              }}
              variant={"complex"}
              hidePageSize={true}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

/**
 * Renders the Scorecards page.
 *
 * @returns The rendered Scorecards page.
 */
export function Scorecards(): React.ReactElement {
  const { t } = useTranslation("scorecards")

  const [tab, setTab] = useState<"scorecards" | "sets">("scorecards")

  const [showNewSet, setShowNewSet] = useState<boolean>(false)
  const [showScorecard, setShowScorecard] = useState<boolean>(false)
  const [showScoretable, setShowScoretable] = useState<boolean>(false)

  const [anchorEl, setAnchorEl] = useState<HTMLElement>()

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const [duplicatedScoreCard, setDuplicatedScoreCard] = useState<ScoreCardList | ScoreCardSetList | null>(null)

  // subHeader action buttons to be passed to scorecard tab
  const scorecardActionButtons = [
    <Button
      key="import_scorecard_button"
      variant="secondary"
      onClick={() => setIsDialogOpen(() => true)}
      disabled={showScorecard || !!duplicatedScoreCard}
      size="regular"
    >
      {t("importBtn")}
    </Button>,
    <Button
      key="create_scorecard_button"
      variant="primary"
      onClick={(e) => setAnchorEl(e?.currentTarget)}
      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
      disabled={!!duplicatedScoreCard}
    >
      {t("createBtn")}
    </Button>,
  ]

  // subHeader action buttons to be passed to scorecard sets tab
  const scorecardsetActionButtons = [
    <Button
      key="create_scorecardset_button"
      variant="primary"
      onClick={() => setShowNewSet(() => true)}
      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
      disabled={!!duplicatedScoreCard}
    >
      {t("createsetBtn")}
    </Button>,
  ]

  return (
    <Grid container spacing={2}>
      <CopyComponentsFlow<"Scorecard">
        key={isDialogOpen.toString()}
        componentName={"Scorecard"}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
      />
      <Grid item xs={12} display={"flex"} justifyContent={"space-between"}>
        <Grid item>
          <Tabs value={tab}>
            <Tab
              label={t("scoreCardSetsTabTitle")}
              value="sets"
              selected={tab === "sets"}
              onClick={() => setTab("sets")}
              size="large"
            />
            <Tab
              label={t("scoreCardTabTitle")}
              value="scorecards"
              selected={tab === "scorecards"}
              onClick={() => setTab("scorecards")}
              size="large"
            />
          </Tabs>
        </Grid>

        <Grid item display={"flex"} alignItems={"flex-end"}>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(undefined)} menuMaxContent>
            <MenuItem
              onClick={() => {
                setShowScorecard(true)
                setAnchorEl(undefined)
              }}
            >
              {t("menuItem.scorecard")}
            </MenuItem>

            <MenuItem
              onClick={() => {
                setShowScoretable(true)
                setAnchorEl(undefined)
              }}
            >
              {t("menuItem.scoreTable")}
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        {tab === "sets" ? (
          <ScoreSetsTab
            shouldShowNewScoreSet={showNewSet}
            onNewScoreSetCancel={() => setShowNewSet(false)}
            duplicatedScoreCard={duplicatedScoreCard as ScoreCardSetList | null}
            setDuplicatedScoreCard={
              setDuplicatedScoreCard as React.Dispatch<React.SetStateAction<ScoreCardSetList | null>>
            }
            subHeaderActionButtons={scorecardsetActionButtons}
          />
        ) : (
          <ScorecardsTab
            duplicatedScoreCard={duplicatedScoreCard}
            setDuplicatedScoreCard={setDuplicatedScoreCard}
            shouldShowNewScorecard={showScorecard}
            onNewScoreCardCancel={() => setShowScorecard(false)}
            shouldShowNewScoreTable={showScoretable}
            onNewScoreTableCancel={() => setShowScoretable(false)}
            subHeaderActionButtons={scorecardActionButtons}
          />
        )}
      </Grid>
    </Grid>
  )
}
